import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Modal, Radio, Select, message } from 'antd';
import styled from 'styled-components';

import DropdownMenuButton from '../../cm/common/DropdownMenuButton';
import BulkUpdateAlertStatusModal from '../../cm/alertList/BulkUpdateAlertStatusModal';
import AssignAlertModal from '../../cm/alertList/AssignAlertModal';
import DownloadDropdown from '../../common/TransactionDetalis/DownloadDropdown';
import ExportHistory from '../../common/TransactionDetalis/ExportHistory';
import {
  TRANSACTION_HISTORY_MODULES,
  FRAML_QUEUE_EXPORT_SOURCE_ID
} from '../../constants/commonConstants';

import FramlHeaderStats from './components/FramlHeaderStats';
import FramlFilterBar from './components/FramlFilterBar';
import FramlCustomerTable from './components/FramlCustomerTable';
import FramlMergeToCaseModal from './components/FramlMergeToCaseModal';
import RefreshWidget from '../../../components/RefreshWidget/RefreshWidget';

import {
  fetchQueue,
  fetchCustomerAlerts,
  setFilters,
  downloadFramlQueueExport,
  createCasesForCustomers,
  mergeCasesForCustomers,
  clearBulkCaseOutcome
} from '../../../redux/actions/framl/landing';
import { bulkCaseOutcomeMessage } from './bulkCaseOutcome';
import {
  getSummary,
  getCustomers,
  getFilters,
  getSort,
  getPagination
} from '../../../redux/selectors/framl/landing';
import { getLiveRiskRefreshInterval } from '../../../services/cm/general';
import { getCurrentUserDepartmentIds } from '../../../redux/selectors/cm/users';
// FIN-11360: load the CM-configurable Level/Status labels — same store the Case
// Summary tab uses (cmWorkFlowStatus) so the Alert Level pill shows configured names.
import {
  fetchAlertLevel,
  fetchWorkFlowStatusList
} from '../../../redux/actions/cm/workFlowStatus';

const Page = styled.div`
  background: #f4f5f8;
  min-height: 100%;
  padding: 22px 26px 40px;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
`;
const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;
const Title = styled.h1`
  font-size: 20px;
  font-weight: 500;
  line-height: 28px;
  color: #29303d;
  margin: 0 0 2px;
`;
const Subtitle = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: #676f7e;
  margin: 0;
`;
const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;
const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e8eaf0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
`;
const EmptyState = styled.p`
  text-align: center;
  color: #8a909c;
  padding: 48px 0;
  margin: 0;
`;
const LiveBar = styled.div`
  & > div {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #676f7e;
  }
  & > div > span:first-child {
    color: #1f9d5b;
    font-weight: 600;
  }
  & > div > span:first-child::before {
    content: '●';
    color: #1f9d5b;
    font-size: 9px;
    margin-right: 4px;
  }
  & > div > span:not(:first-child)::before {
    content: '·';
    color: #c0c4cc;
    margin-right: 6px;
  }
  & .ant-btn {
    margin-left: 8px;
    border-radius: 8px;
  }
`;
const ActionsWrap = styled.span`
  & .ant-btn {
    background: #7d97e0;
    border-color: #7d97e0;
    color: #fff;
    border-radius: 8px;
    font-weight: 500;
  }
  & .ant-btn[disabled] {
    background: #c2cdee;
    border-color: #c2cdee;
    color: #fff;
  }
`;

// FIN-11360: auto-refresh interval config — same general-settings key as the
// Case Summary Live-Risk panel (JSON {refreshMinutes}); default 3 min.
const LIVE_RISK_REFRESH_CONFIG_KEY = 'LIVE_RISK_REFRESH_INTERVAL';
const LIVE_RISK_REFRESH_MS_DEFAULT = 3 * 60 * 1000;

export function isFilterActive(filters) {
  const f = filters || {};
  const search = f.search || '';
  if (search.length > 0) return true;
  if ((f.sources || []).length > 0) return true;
  if ((f.statuses || []).length > 0) return true;
  if ((f.risk || []).length > 0) return true;
  return false;
}

const CASE_TYPE_FRAUD = 'Fraud Only';
const CASE_TYPE_AML = 'AML Only';
const CASE_TYPE_COMBINED = 'Combined';

function matchesCaseType(a, caseType) {
  if (caseType === CASE_TYPE_FRAUD) return a.source === 'Fraud';
  if (caseType === CASE_TYPE_AML) return a.source === 'AML';
  return a.source === 'Fraud' || a.source === 'AML';
}

function getEligibleAlerts(alerts, caseType) {
  return (alerts || []).filter((a) => {
    if (a.status === 'ASSIGNED') return false;
    // FIN-13684: an alert already in a case (live caseId from the expand read) is not
    // eligible — the BE rejects it with ALERTS_ALREADY_CASED. Same rule the merge plan uses.
    if (a.caseId) return false;
    return matchesCaseType(a, caseType);
  });
}

// FIN-13958 — the alerts left out above because they are already in a case. They never reach the
// BE, so the outcome only knows about them if the page says so; otherwise a customer whose alerts
// are all cased vanishes from the toast ("1 case(s) created" / "Alerts merged successfully").
const toCasedLinks = (alerts) =>
  alerts.map((a) => ({ alertId: a.alertId, caseId: a.caseId }));

// FIN-13960 — "assigned" for the two bulk modals that promise to skip assigned alerts (Bulk Status Update,
// Bulk Assign) means HAS AN ASSIGNEE, the same definition the queue's Assignment chip uses
// (assignmentAggregates: assigned_username_id IS NOT NULL). The workflow status alone is not enough: an
// assigned alert moves on to WORK_IN_PROGRESS / ON_HOLD / … while keeping its assignee, and the BE bulk
// endpoint does not skip anything itself, so whatever this lets through gets updated.
export function isUnassignedForBulk(a) {
  return a.status !== 'ASSIGNED' && a.assignedUsernameId == null;
}

export const NO_ELIGIBLE_BULK_MSG =
  'No unassigned alerts for the selected customers — alerts already assigned to an investigator are skipped.';

function pluralAlert(n) {
  return n === 1 ? 'eligible alert' : 'eligible alerts';
}

function FramlLandingPage({
  summary,
  customers,
  filters,
  sort,
  pagination,
  customerAlerts,
  customerAlertsMeta,
  accessDenied,
  exportInProgress,
  workFlowStatusList,
  alertLevels,
  createInFlight,
  mergeInFlight,
  bulkCaseOutcome,
  departments,
  fetchQueue: doFetch,
  fetchCustomerAlerts: doFetchAlerts,
  createCasesForCustomers: doCreateCases,
  mergeCasesForCustomers: doMergeCases,
  clearBulkCaseOutcome: doClearBulkCaseOutcome,
  fetchAlertLevel: doFetchAlertLevel,
  fetchWorkFlowStatusList: doFetchWorkFlowStatusList,
  setFilters: doSetFilters,
  downloadFramlQueueExport: doDownloadExport
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pendingBulkOpen, setPendingBulkOpen] = useState(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const [pendingMergeOpen, setPendingMergeOpen] = useState(null);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);

  const [pendingCreateOpen, setPendingCreateOpen] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [caseType, setCaseType] = useState(CASE_TYPE_COMBINED);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  const [pendingAssignOpen, setPendingAssignOpen] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  // FIN-11360: auto-refresh interval is CM-configurable (same general-settings key
  // the Case Summary Live-Risk panel uses); falls back to 3 min if unset/unreadable.
  const [refreshMs, setRefreshMs] = useState(LIVE_RISK_REFRESH_MS_DEFAULT);
  const [historyOpen, setHistoryOpen] = useState(false);

  // FIN-13175 — lifts the FIN-12987 counter pattern (FramlCustomerDetailPage.js) onto the
  // queue: ExportHistory refetches on [isActive, refreshKey], but this modal previously
  // passed refreshKey={historyOpen ? 1 : 0} — a constant derived from the modal's OPEN
  // state, carrying no completion signal. A job submitted after the modal's one-shot fetch
  // (or with the modal already open) never triggered a refetch, so Download History sat
  // stale until a manual page refresh. Bump the counter on submission SUCCESS (the saga
  // strips + invokes onSuccess once the job row is written — see sagas/framl/landing.js),
  // the same hook point FIN-13443 wired for the customer-detail tabs.
  const [downloadHistoryRefreshKey, setDownloadHistoryRefreshKey] = useState(0);
  const handleDownloadComplete = () =>
    setDownloadHistoryRefreshKey((k) => k + 1);

  // FIN-12427: "Updated Xago" is a CLIENT-CAPTURED timestamp of when the FE last received
  // a queue response — not the BE snapshot time. Under Draft v5 (FIN-12421) UI_REFRESH is
  // dropped, so `summary.lastComputedAt` reflects the last REAL event write (which can be
  // arbitrarily stale). What the user actually wants to see is "when did I last poll the
  // BE" — updated on every successful GET. The `summary` reference changes per fetch (the
  // reducer produces a fresh object) so this useEffect fires once per successful response.
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  useEffect(() => {
    if (summary != null) setLastFetchedAt(Date.now());
  }, [summary]);

  // FIN-11360 Task 13: shared filter/sort params for both download handlers below.
  // sort is stored as { sortField, sortDir } (see redux/reducers/framl/landing.js
  // initialState + SET_SORT) — the BE export param names match 1:1.
  const buildDownloadParams = () => ({
    ...(filters.source ? { source: filters.source } : {}),
    ...(filters.riskBand ? { riskBand: filters.riskBand } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    ...(sort && sort.sortField ? { sortField: sort.sortField } : {}),
    ...(sort && sort.sortDir ? { sortDir: sort.sortDir } : {})
  });

  // FIN-11360 Task 13: "Download Current Page" — offset/limit derived from the
  // queue's own pagination state (0-indexed page, matches the queue query).
  const handleDownloadCurrentPage = () => {
    const size = pagination?.size ?? 10;
    const offset = (pagination?.page ?? 0) * size;
    doDownloadExport({
      ...buildDownloadParams(),
      offset,
      limit: size,
      // download:true selects the BE FramlQueueExportController sync-CSV branch
      // (offset+limit+download==true) — without it the BE falls through to the
      // async "download all" job path even though offset/limit are present.
      download: true,
      // sent explicitly to avoid a BE fallback warn.
      moduleId: FRAML_QUEUE_EXPORT_SOURCE_ID
    });
  };

  // FIN-11360 Task 13: "Download All Matching" — no offset/limit, BE resolves
  // the full filtered set (async "download all" job path).
  const handleDownloadAll = () => {
    doDownloadExport({
      ...buildDownloadParams(),
      downloadAll: true,
      moduleId: FRAML_QUEUE_EXPORT_SOURCE_ID,
      // FIN-13175 — bump Download History's refreshKey once the submit resolves (new job
      // OR the dedup-hit existing one), mirroring the onSuccess contract sagas/tmCommon.js
      // already exposes for the customer-detail tabs.
      onSuccess: handleDownloadComplete
    });
  };

  useEffect(() => {
    doFetch();
    // FIN-11360: load the CM-configurable labels so the Alert Level pill uses the
    // admin displayName/color and underlying-alert statuses map to their config names.
    doFetchAlertLevel();
    doFetchWorkFlowStatusList();
  }, []);

  // FIN-11360: read the configurable auto-refresh interval once on mount.
  useEffect(() => {
    let active = true;
    getLiveRiskRefreshInterval()
      .then((data) => {
        const item = ((data && data.items) || []).find(
          (i) => i.configKey === LIVE_RISK_REFRESH_CONFIG_KEY
        );
        let minutes = null;
        try {
          minutes = item && JSON.parse(item.configValue).refreshMinutes;
        } catch (e) {
          minutes = null;
        }
        if (active && typeof minutes === 'number' && minutes > 0) {
          setRefreshMs(minutes * 60 * 1000);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // FIN-13960 review R3 — with the assignee rule, a selection whose alerts are all assigned is common.
  // Opening the modal with nothing eligible showed an empty list (Bulk Status) or the PREVIOUS run's
  // alerts (AssignAlertModal only refreshes its tags when selectedIds is non-empty), and Assign then
  // sent alertIds: [] → BE error. Say so instead of opening.
  const hasEligibleForBulk = (keys) =>
    keys.some((key) => (customerAlerts[key] || []).some(isUnassignedForBulk));

  useEffect(() => {
    if (!pendingBulkOpen) return;
    const allLoaded = pendingBulkOpen.every(
      (key) => customerAlerts[key] !== undefined
    );
    if (allLoaded) {
      if (!hasEligibleForBulk(pendingBulkOpen)) {
        message.info(NO_ELIGIBLE_BULK_MSG);
        setPendingBulkOpen(null);
        return;
      }
      setBulkModalOpen(true);
    }
  }, [customerAlerts, pendingBulkOpen]);

  useEffect(() => {
    if (!pendingMergeOpen) return;
    const allLoaded = pendingMergeOpen.every(
      (key) => customerAlerts[key] !== undefined
    );
    if (allLoaded) {
      setMergeModalOpen(true);
    }
  }, [customerAlerts, pendingMergeOpen]);

  useEffect(() => {
    if (!pendingCreateOpen) return;
    const allLoaded = pendingCreateOpen.every(
      (key) => customerAlerts[key] !== undefined
    );
    if (allLoaded) {
      setCreateModalOpen(true);
    }
  }, [customerAlerts, pendingCreateOpen]);

  useEffect(() => {
    if (!pendingAssignOpen) return;
    const allLoaded = pendingAssignOpen.every(
      (key) => customerAlerts[key] !== undefined
    );
    if (allLoaded) {
      if (!hasEligibleForBulk(pendingAssignOpen)) {
        message.info(NO_ELIGIBLE_BULK_MSG);
        setPendingAssignOpen(null);
        return;
      }
      setAssignModalOpen(true);
    }
  }, [customerAlerts, pendingAssignOpen]);

  // FIN-12887: a row-expand caches only ONE server page (size 20) under
  // customerAlerts[key]. Bulk actions need the customer's full alert set, so refetch
  // at the BE max (100) when the cached slice is smaller than the known total.
  const needsFullAlerts = (key) => {
    const items = customerAlerts[key];
    if (items === undefined) return true;
    const meta = (customerAlertsMeta || {})[key];
    return !!meta && meta.total > items.length;
  };

  const handleBulkStatusUpdate = () => {
    selectedRowKeys.forEach((key) => {
      if (needsFullAlerts(key)) {
        doFetchAlerts(key, 0, 100);
      }
    });
    setPendingBulkOpen(selectedRowKeys);
  };

  const handleBulkAssign = () => {
    selectedRowKeys.forEach((key) => {
      if (needsFullAlerts(key)) {
        doFetchAlerts(key, 0, 100);
      }
    });
    setPendingAssignOpen(selectedRowKeys);
  };

  const handleAssignModalCancel = () => {
    setAssignModalOpen(false);
    setPendingAssignOpen(null);
  };

  const handleMergeToCase = () => {
    selectedRowKeys.forEach((key) => {
      if (needsFullAlerts(key)) {
        doFetchAlerts(key, 0, 100);
      }
    });
    setPendingMergeOpen(selectedRowKeys);
  };

  const handleMergeModalCancel = () => {
    setMergeModalOpen(false);
    setPendingMergeOpen(null);
  };

  // Merge each customer's selected alerts into that customer's chosen open case.
  const handleMergePlan = (plan) => {
    const actionable = (plan || []).filter(
      (p) => p.caseId && (p.alertIds || []).length > 0
    );
    // FIN-13083 — an empty plan used to be a silent success: forEach over [] dispatched
    // nothing, so no request, no toast and the dialog stayed open. The Merge button is now
    // disabled in that state, so reaching here means something is out of sync — say so
    // instead of returning quietly.
    if (actionable.length === 0) {
      message.error(
        'Nothing to merge — the selected alerts are already in a case.'
      );
      return;
    }
    // FIN-13958 — ONE action for every customer; the saga reports ONE folded outcome.
    doMergeCases({
      plans: actionable.map((p) => ({
        customerKey: p.customerKey,
        alertIds: p.alertIds,
        caseId: p.caseId
      })),
      alreadyCased: mergeCustomers.flatMap((c) => c.casedAlerts)
    });
  };

  // FIN-13958 — ONE message for the whole Create Case / Merge to Case action. Each per-customer
  // request used to report through the shared cmConvertAlertsToCase / cmMergeAlertsToCase boolean
  // flags: one toast per failed request (N customers → N "Already part of a case" pop-ups at once),
  // and two failures landing before this effect ran (flag true → true) dropped one of them.
  // Messages keep the FIN-11360 / FIN-12898 / FIN-13021 / FIN-13684 wording, aggregated.
  useEffect(() => {
    if (!bulkCaseOutcome) return;
    const { level, text } = bulkCaseOutcomeMessage(bulkCaseOutcome);
    message[level](text);
    if (bulkCaseOutcome.kind === 'merge') {
      handleMergeModalCancel();
      if (bulkCaseOutcome.merged > 0) setSelectedRowKeys([]);
    } else if (bulkCaseOutcome.created.length > 0) {
      // PRD: toast, modal closes, row selection clears.
      handleCreateModalCancel();
      setSelectedRowKeys([]);
    }
    // Nothing created → the modal stays open (FIN-13684); the saga has refetched the customers'
    // alerts, so the eligible counts drop to what is actually still eligible.
    doClearBulkCaseOutcome();
  }, [bulkCaseOutcome]);

  const handleCreateCase = () => {
    selectedRowKeys.forEach((key) => {
      if (needsFullAlerts(key)) {
        doFetchAlerts(key, 0, 100);
      }
    });
    setPendingCreateOpen(selectedRowKeys);
  };

  const handleCreateModalCancel = () => {
    setCreateModalOpen(false);
    setPendingCreateOpen(null);
    setSelectedDepartmentId(null);
    setCaseType(CASE_TYPE_COMBINED);
  };

  const handleCreateCaseSubmit = () => {
    // FIN-13958 — ONE action for every customer with eligible alerts; the saga reports ONE outcome.
    const plans = pendingCreateOpen
      .map((key) => ({
        customerKey: key,
        alertIds: getEligibleAlerts(customerAlerts[key], caseType).map(
          (a) => a.alertId
        )
      }))
      .filter((p) => p.alertIds.length > 0);
    if (plans.length === 0) return;
    const alreadyCased = pendingCreateOpen.flatMap((key) =>
      toCasedLinks(
        (customerAlerts[key] || []).filter(
          (a) => a.caseId && matchesCaseType(a, caseType)
        )
      )
    );
    doCreateCases({
      plans,
      departmentId: selectedDepartmentId,
      caseType,
      alreadyCased
    });
  };

  // Memoized so the refs stay stable across renders — same fix as the Bulk Assign
  // path (B1). BulkUpdateAlertStatusModal is always-mounted and its mount effect
  // keys on [visible, selectedIds]; a fresh array each render re-fired that effect
  // (which dispatches bulkUpdateAlertStatusReset → redux → parent re-render) →
  // "Maximum update depth exceeded" render loop. FIN-11360 E20.
  const allSelectedAlerts = useMemo(
    () =>
      pendingBulkOpen
        ? pendingBulkOpen.flatMap((key) => {
            const alerts = customerAlerts[key] || [];
            return alerts.filter(isUnassignedForBulk).map((a) => ({
              alert: { id: a.alertId },
              metadata: { status: a.status }
            }));
          })
        : [],
    [pendingBulkOpen, customerAlerts]
  );

  const selectedAlertIds = useMemo(
    () => allSelectedAlerts.map((a) => a.alert.id),
    [allSelectedAlerts]
  );

  // FIN-11360 Bulk Assign: reuse the CM assign flow (BE behaviour + API). Only
  // unassigned alerts under the selected customers are eligible (mirrors the
  // amber warning + the bulk-status path; FIN-13960 — "unassigned" = no assignee).
  // Memoized so the refs stay stable across renders — AssignAlertModal is
  // always-mounted (see below) and its fetch effect keys on selectedIds; a
  // fresh array each render would dispatch every render (render loop). FIN-11360 B1.
  // FIN-13933 — one alert can sit under several customers (one contribution row per party), so a
  // multi-customer selection repeated it; keep the first occurrence so counts and the request are per alert.
  const assignSelectedAlerts = useMemo(() => {
    if (!pendingAssignOpen) return [];
    const seen = new Set();
    return pendingAssignOpen.flatMap((key) => {
      const alerts = customerAlerts[key] || [];
      return alerts
        .filter(isUnassignedForBulk)
        .filter((a) => !seen.has(a.alertId) && seen.add(a.alertId))
        .map((a) => ({
          alert: { id: a.alertId },
          metadata: {
            status: a.status,
            assignedUsernameId: a.assignedUsernameId || null
          }
        }));
    });
  }, [pendingAssignOpen, customerAlerts]);
  const assignAlertIds = useMemo(
    () => assignSelectedAlerts.map((a) => a.alert.id),
    [assignSelectedAlerts]
  );

  // FIN-11360 Merge-to-Case: per-customer merge targets — each selected customer with
  // its unassigned alert IDs; the modal fetches each customer's open cases + lets the
  // user pick which case to append that customer's alerts to.
  // FIN-12930: memoized — an unmemoized rebuild handed the modal a FRESH array every
  // parent render (auto-refresh tick, any state change), refiring its fetch effect and
  // resetting the user's radio picks. `customers` is deliberately NOT a dep: the queue
  // auto-refresh replaces that array every poll, and it only feeds display names here.
  const mergeCustomers = useMemo(
    () =>
      pendingMergeOpen
        ? pendingMergeOpen.map((key) => {
            const row = (customers || []).find((c) => c.customerKey === key);
            const alerts = customerAlerts[key] || [];
            return {
              customerKey: key,
              customerName: row ? row.customerName : key,
              // FIN-12898: alerts already in a case (live caseId from the expand read)
              // are excluded pre-submit — the BE would skip them anyway.
              alertIds: alerts
                .filter((a) => a.status !== 'ASSIGNED' && !a.caseId)
                .map((a) => a.alertId),
              // FIN-13083: the excluded ones, so the modal can SAY why there is nothing to
              // merge. When every alert is already cased alertIds is [], and the dialog used
              // to show "· 0 alert(s)" with an enabled Merge button that did nothing.
              casedAlerts: toCasedLinks(alerts.filter((a) => a.caseId))
            };
          })
        : [],
    [pendingMergeOpen, customerAlerts] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fraudCount = pendingCreateOpen
    ? pendingCreateOpen.reduce((acc, key) => {
        return (
          acc + getEligibleAlerts(customerAlerts[key], CASE_TYPE_FRAUD).length
        );
      }, 0)
    : 0;
  const amlCount = pendingCreateOpen
    ? pendingCreateOpen.reduce((acc, key) => {
        return (
          acc + getEligibleAlerts(customerAlerts[key], CASE_TYPE_AML).length
        );
      }, 0)
    : 0;
  const combinedCount = pendingCreateOpen
    ? pendingCreateOpen.reduce((acc, key) => {
        return (
          acc +
          getEligibleAlerts(customerAlerts[key], CASE_TYPE_COMBINED).length
        );
      }, 0)
    : 0;

  const currentEligibleCount =
    caseType === CASE_TYPE_FRAUD
      ? fraudCount
      : caseType === CASE_TYPE_AML
      ? amlCount
      : combinedCount;

  // FIN-13958 — also locked while the previous submit is in flight (a double click used to send the
  // same alerts twice; the second batch 409'd once per customer).
  const createBtnDisabled =
    !selectedDepartmentId || currentEligibleCount === 0 || createInFlight;

  const numSelectedCustomers = pendingCreateOpen ? pendingCreateOpen.length : 0;

  const ACTIONS_OPTIONS = [
    { label: 'Bulk Assign', value: 'bulk-assign', onClick: handleBulkAssign },
    {
      label: 'Bulk Status Update',
      value: 'bulk-status-update',
      onClick: handleBulkStatusUpdate
    },
    {
      label: 'Merge to Case',
      value: 'merge-to-case',
      onClick: handleMergeToCase
    },
    { label: 'Create Case', value: 'create-case', onClick: handleCreateCase }
  ];

  const filterActive = isFilterActive(filters);
  const empty = customers.length === 0;

  return (
    <Page>
      <HeaderRow>
        <div>
          <Title>FRAML View</Title>
          <Subtitle>
            Unified queue — Fraud &amp; AML signals converged per customer
          </Subtitle>
        </div>
        <RightControls>
          <LiveBar>
            <RefreshWidget
              onRefresh={() => doFetch()}
              intervalMs={refreshMs}
              cooldownMs={30000}
              lastFetchedAt={lastFetchedAt}
            />
          </LiveBar>
          <DownloadDropdown
            onDownloadCurrentPage={handleDownloadCurrentPage}
            onDownloadAll={handleDownloadAll}
            onViewHistory={() => setHistoryOpen(true)}
            isDownloading={exportInProgress}
            isDisabled={empty}
            currentPageCount={customers.length}
            totalCount={pagination?.total ?? 0}
          />
          <ActionsWrap>
            <DropdownMenuButton
              label="Actions"
              options={ACTIONS_OPTIONS}
              isDisabled={selectedRowKeys.length === 0}
            />
          </ActionsWrap>
        </RightControls>
      </HeaderRow>

      <FramlHeaderStats
        summary={summary}
        activeSource={filters.source}
        onTileFilter={(s) => doSetFilters({ source: s })}
      />

      <Card>
        <FramlFilterBar />

        {accessDenied && (
          <EmptyState>
            You don&apos;t have access to FRAML View. Contact your
            administrator.
          </EmptyState>
        )}
        {!accessDenied && empty && !filterActive && (
          <EmptyState>No FRAML queue items yet</EmptyState>
        )}
        {!accessDenied && empty && filterActive && (
          <EmptyState>No customers match</EmptyState>
        )}

        {!empty && (
          <FramlCustomerTable
            selectedRowKeys={selectedRowKeys}
            onSelectChange={setSelectedRowKeys}
          />
        )}
      </Card>

      <BulkUpdateAlertStatusModal
        visible={bulkModalOpen}
        handleExit={() => {
          setBulkModalOpen(false);
          setPendingBulkOpen(null);
        }}
        selectedIds={selectedAlertIds}
        selectedAlerts={allSelectedAlerts}
        workFlowStatusList={workFlowStatusList}
        confirmBeforeSubmit
        titleOverride="Bulk status update"
        unassignedOnlyWarning
        onBulkSuccess={() => {
          // FIN-12030 (D8): FRAML result lifecycle — clear selection + refetch queue.
          // handleExit + bulkUpdateAlertStatusReset still fire inside the modal's
          // result effect (shared with CM path); only the FRAML-specific actions live here.
          setSelectedRowKeys([]);
          setPendingBulkOpen(null);
          doFetch();
        }}
      />

      {/* Always-mounted (visible toggles) — matches CM AlertList + the sibling
          BulkUpdateAlertStatusModal. A conditional `{open && …}` mount would
          hit the modal's mount-time "close when no tags" effect and auto-exit. */}
      <AssignAlertModal
        visible={assignModalOpen}
        handleExit={handleAssignModalCancel}
        selectedIds={assignAlertIds}
        selectedAlerts={assignSelectedAlerts}
        unassignedOnlyWarning
        onAssignSuccess={() => {
          // FIN-11360: reuse CM assign API; FRAML clears its own selection +
          // refetches its queue on success (handleExit fires inside the modal too).
          setSelectedRowKeys([]);
          setPendingAssignOpen(null);
          doFetch();
        }}
      />

      <FramlMergeToCaseModal
        open={mergeModalOpen}
        // Review R2 — locked while a merge is in flight (a double click used to re-send the same alerts).
        submitting={mergeInFlight}
        customers={mergeCustomers}
        onMerge={handleMergePlan}
        onCancel={handleMergeModalCancel}
      />

      <Modal
        title="Create Case"
        visible={createModalOpen}
        destroyOnClose
        onCancel={handleCreateModalCancel}
        footer={[
          <Button key="cancel" onClick={handleCreateModalCancel}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            disabled={createBtnDisabled}
            onClick={handleCreateCaseSubmit}>
            Create Case
          </Button>
        ]}>
        <p>
          {`One case will be created per selected customer (${numSelectedCustomers})`}
        </p>
        <Radio.Group
          value={caseType}
          onChange={(e) => setCaseType(e.target.value)}>
          <Radio value={CASE_TYPE_FRAUD}>Fraud Only</Radio>
          <Radio value={CASE_TYPE_AML}>AML Only</Radio>
          <Radio value={CASE_TYPE_COMBINED}>Combined (Fraud + AML)</Radio>
        </Radio.Group>
        <p>
          {`${fraudCount} ${pluralAlert(fraudCount)} for Fraud | `}
          {`${amlCount} ${pluralAlert(amlCount)} for AML | `}
          {`${combinedCount} ${pluralAlert(combinedCount)} total`}
        </p>
        <Select
          style={{ width: '100%', marginTop: 16 }}
          placeholder="Select department for the new case"
          value={selectedDepartmentId}
          onChange={(val) => setSelectedDepartmentId(val)}>
          {/* FIN-12941 — stable alphabetical order (BE returns insertion order). */}
          {[...departments]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map((dept) => (
              <Select.Option key={dept.id} value={dept.id}>
                {dept.name}
              </Select.Option>
            ))}
        </Select>
      </Modal>

      <Modal
        title="Download History"
        visible={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={720}>
        <ExportHistory
          module={TRANSACTION_HISTORY_MODULES.FRAML_CUSTOMER_QUEUE_EXPORT}
          isActive={historyOpen}
          title="Download History"
          refreshKey={downloadHistoryRefreshKey}
          alertLevels={alertLevels}
          workFlowStatusList={workFlowStatusList}
        />
      </Modal>
    </Page>
  );
}

FramlLandingPage.propTypes = {
  summary: PropTypes.object,
  customers: PropTypes.array,
  filters: PropTypes.object,
  sort: PropTypes.object,
  pagination: PropTypes.object,
  customerAlerts: PropTypes.object,
  customerAlertsMeta: PropTypes.object,
  accessDenied: PropTypes.bool,
  exportInProgress: PropTypes.bool,
  workFlowStatusList: PropTypes.array,
  alertLevels: PropTypes.array,
  createInFlight: PropTypes.bool,
  mergeInFlight: PropTypes.bool,
  bulkCaseOutcome: PropTypes.object,
  departments: PropTypes.array,
  fetchQueue: PropTypes.func.isRequired,
  fetchCustomerAlerts: PropTypes.func.isRequired,
  createCasesForCustomers: PropTypes.func.isRequired,
  mergeCasesForCustomers: PropTypes.func.isRequired,
  clearBulkCaseOutcome: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  downloadFramlQueueExport: PropTypes.func.isRequired
};

FramlLandingPage.defaultProps = {
  summary: {},
  customers: [],
  filters: {},
  sort: {},
  pagination: {},
  customerAlerts: {},
  customerAlertsMeta: {},
  accessDenied: false,
  exportInProgress: false,
  workFlowStatusList: [],
  createInFlight: false,
  mergeInFlight: false,
  bulkCaseOutcome: null,
  departments: []
};

function mapStateToProps(state) {
  const currentUserDepartmentIds = getCurrentUserDepartmentIds(state);
  const userDepartments = (state.cmDepartments?.list || []).filter(({ id }) =>
    currentUserDepartmentIds.includes(id)
  );
  return {
    summary: getSummary(state),
    customers: getCustomers(state),
    filters: getFilters(state),
    sort: getSort(state),
    pagination: getPagination(state),
    customerAlerts: state.framlLanding.customerAlerts,
    customerAlertsMeta: state.framlLanding.customerAlertsMeta,
    accessDenied: state.framlLanding.error?.status === 403,
    exportInProgress: state.framlLanding.exportInProgress,
    workFlowStatusList: state.cmWorkFlowStatus?.workFlowStatusList || [],
    // FIN-13510 — the FRAML queue Download History panel's "Filters Applied" resolves the
    // riskBand filter's display label through this, so a renamed band (e.g. "Good" -> "Medium")
    // shows correctly instead of the default name.
    alertLevels: state.cmWorkFlowStatus?.alertLevels || [],
    // FIN-13958 — the FRAML bulk Create Case / Merge outcome (one per action, not per request).
    createInFlight: !!state.framlLanding.createInFlight,
    mergeInFlight: !!state.framlLanding.mergeInFlight,
    bulkCaseOutcome: state.framlLanding.bulkCaseOutcome || null,
    departments: userDepartments
  };
}

const mapDispatchToProps = {
  fetchQueue,
  fetchCustomerAlerts,
  createCasesForCustomers,
  mergeCasesForCustomers,
  clearBulkCaseOutcome,
  fetchAlertLevel,
  fetchWorkFlowStatusList,
  setFilters,
  downloadFramlQueueExport
};

export default connect(mapStateToProps, mapDispatchToProps)(FramlLandingPage);
